import { Box } from '@mui/material';
import { type WithRouter, withRouter } from 'found';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { spaces } from '../../assets/styles/themes/tokens';
import LoginFooter from '../../components/login/LoginFooter';
import LoginForm from '../../components/login/LoginForm';
import PageContainer from '../../components/login/PageContainer';
import OnboardingHeader from '../../components/onboarding/OnboardingHeader';
import { errorCodes, LoginSources, routes } from '../../constants';
import RedirectIfLoggedIn from '../../router/redirect/RedirectIfLoggedIn';
import services from '../../services';
import { trackingEvents } from '../../services/tracking/trackConfig';
import useScreenSize from '../../utils/useScreenSize';

interface OwnProps {
	match: {
		location: {
			query: {
				redirect?: string;
			};
		};
	};
}
type LoginPageProps = OwnProps & WithRouter;

const determineLoginFailureEvent = (source: LoginSources, errorCode?: number) => {
	if (source === LoginSources.facebook) {
		if (errorCode === errorCodes.fbEmailDoesntExist) {
			return trackingEvents.loginFailure.facebookIncorrectEmail;
		}

		return trackingEvents.loginFailure.facebookCancelled;
	}

	if (errorCode === errorCodes.ipRestriction) {
		return trackingEvents.loginFailure.incorrectIP;
	}

	if (errorCode === errorCodes.userNotValidated) {
		return trackingEvents.loginFailure.notVerified;
	}
	return trackingEvents.loginFailure.incorrectEmail;
};

const LoginPage: React.FC<LoginPageProps> = (props) => {
	const { t } = useTranslation();
	const { isXsScreen } = useScreenSize();

	const title = t('Welcome back');
	const subtitle = t('Log in to create your lesson.');

	const handleLoginSuccess = useCallback(
		(source: LoginSources) => {
			const redirect = props.match.location.query.redirect;
			const isSafeRedirect =
				redirect &&
				redirect.startsWith('/') &&
				!redirect.startsWith('//') &&
				!redirect.startsWith(`/${routes.login.root}`);

			if (isSafeRedirect) {
				props.router.push(redirect);
			} else {
				props.router.push(`/${routes.dashboard.root}`);
			}

			if (source !== LoginSources.facebook) {
				services.track.eventTrack(trackingEvents.loginSuccess.email);
			}
		},
		[props.router, props.match]
	);

	const handleLoginFailure = useCallback(
		(source: LoginSources, statusCode: number, errorCode?: number) => {
			if (errorCode === errorCodes.ipRestriction) {
				props.router.push(`/${routes.ipRangeError.root}`);
			}
			const event = determineLoginFailureEvent(source, errorCode);

			const payload = {
				other: {
					login_type: source === LoginSources.facebook ? 'FB' : 'Email',
					status_code: statusCode,
				},
			};
			services.track.eventTrack(event, payload);
		},
		[props.router]
	);

	return (
		<RedirectIfLoggedIn>
			<PageContainer>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: isXsScreen ? spaces.xl : spaces.xxxl,
					}}
				>
					<OnboardingHeader title={title} subtitle={subtitle} />
					<LoginForm onLoginSuccess={handleLoginSuccess} onLoginFailure={handleLoginFailure} />
				</Box>
				<LoginFooter isSmallScreen={isXsScreen} />
			</PageContainer>
		</RedirectIfLoggedIn>
	);
};

export default withRouter(LoginPage);
